"use client";

import { useEffect, useMemo, useState } from "react";
import { Block, BlockType, uuid } from "../types/screenplay-editor.types";

export interface UseSceneScriptOptions {
  scene_url: string;
  fetchOptions?: RequestInit;
}

export function useSceneScript(options: UseSceneScriptOptions) {
  const [sceneContent, setSceneContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchScene = async () => {
      try {
        const response = await fetch(options.scene_url, options.fetchOptions);
        if (response.ok) {
          const text = await response.text();
          if (isMounted) {
            setSceneContent(text);
          }
        } else {
          if (isMounted) {
            setError(`Failed to fetch scene: ${response.statusText}`);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "An unknown error occurred");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchScene();

    return () => {
      isMounted = false;
    };
  }, [options.scene_url]);

  const blocks = useMemo(() => {
    if (!sceneContent) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(sceneContent, "text/html");
    const divs = Array.from(doc.querySelectorAll("div"));
    const parsedBlocks: Block[] = [];

    const typeMap: Record<string, BlockType> = {
      divtype0: "SCENE_HEADING",
      divtype2: "ACTION",
      divtype3: "CHARACTER",
      divtype4: "PARENTHETICAL",
      divtype5: "DIALOGUE",
      divtype6: "TRANSITION",
    };

    divs.forEach((div) => {
      const divText = div.textContent?.trim() || "";
      if (!divText && !div.classList.contains("divtype0")) return;

      let type: BlockType = "ACTION";
      for (const className of Array.from(div.classList)) {
        if (typeMap[className]) {
          type = typeMap[className];
          break;
        }
      }

      const idAttr = div.getAttribute("id");
      const blockId =
        idAttr && idAttr.startsWith("par")
          ? idAttr.substring(3)
          : idAttr || uuid();

      // Extract scene metadata if it's a heading
      let sceneNumber: string | undefined;
      let sceneType: "INT." | "EXT." | "INT/EXT." | undefined;
      let timeOfDay: any;
      let blockText = divText;

      if (type === "SCENE_HEADING") {
        sceneNumber = div.getAttribute("data-scene") || undefined;
        // Try to parse: TYPE. LOCATION - TIME
        const typeMatch = blockText.match(/^(INT\.?\/EXT\.?|INT\.?|EXT\.?|I\/E)\s+/i);
        if (typeMatch) {
          const matchedType = typeMatch[1].toUpperCase();
          if (matchedType.includes("INT") && matchedType.includes("EXT")) sceneType = "INT/EXT.";
          else if (matchedType.includes("EXT")) sceneType = "EXT.";
          else sceneType = "INT.";
          
          blockText = blockText.substring(typeMatch[0].length).trim();
        }

        const timeMatch = blockText.match(/\s+-\s+(DAY|NIGHT|MORNING|EVENING|AFTERNOON|LATER|MOMENTS LATER|CONTINUOUS)$/i);
        if (timeMatch) {
          timeOfDay = timeMatch[1].toUpperCase();
          blockText = blockText.substring(0, timeMatch.index).trim();
        }
        
        // Remove leading/trailing dashes if any left
        blockText = blockText.replace(/^-+\s*|\s*-+$/g, "").trim();
      }

      parsedBlocks.push({ 
        id: blockId, 
        type, 
        text: blockText,
        sceneNumber,
        sceneType,
        timeOfDay
      });
    });

    return parsedBlocks;
  }, [sceneContent]);

  return {
    blocks,
    isLoading,
    error,
    sceneContent,
  };
}
